import type { EffectRecord } from './reactive-v2'
import { isActive, isPaused, isScheduled, setScheduled, clearScheduled } from './effect-flags'
import { getRequestContext } from './request-context'
import { runEffectV2 } from './reactive-v2'
import type { ReactiveEffectLike } from './lifecycle'

export type ScheduleEffectFn = (effect: EffectRecord) => void

// ---------------------------------------------------------------------------
// Per-request flush queue
// ---------------------------------------------------------------------------
// We piggyback on the existing RequestContext.effectQueue for SSR isolation.
// The V2 scheduler stores EffectRecord objects there (they satisfy the
// ReactiveEffectLike duck type well enough for queue membership).

let isFlushing = false

/**
 * Queue an effect for deferred microtask execution.
 *
 * Deduplication: each effect has a unique numeric ID. The SCHEDULED bit on
 * `effect.flags` ensures the same effect isn't queued twice per flush cycle.
 */
export function scheduleEffect(effect: EffectRecord): void {
    if (!isActive(effect.flags)) return
    if (isPaused(effect.flags)) return
    if (isScheduled(effect.flags)) return  // already in queue

    const ctx = getRequestContext()
    effect.flags = setScheduled(effect.flags)
    ctx.effectQueue.add(effect as unknown as ReactiveEffectLike)

    if (ctx.isBatching) {
        // In batch mode: marked scheduled, queue holds it, but no microtask yet.
        return
    }

    if (!ctx.isFlushPending) {
        ctx.isFlushPending = true
        Promise.resolve().then(flushScheduler)
    }
}

/**
 * Flush all queued V2 effects.
 *
 * Called by the microtask promise or by sync() / flushSchedulerSync().
 * Effects are run in insertion order. Re-queued effects (from re-entrant
 * triggers) are deferred to the next flush cycle.
 */
export function flushScheduler(): void {
    if (isFlushing) return  // re-entrancy guard
    isFlushing = true

    const ctx = getRequestContext()
    ctx.isFlushPending = false

    try {
        // Drain: clone queue, clear it, then run each effect.
        // Effects may re-trigger during run — those go into a fresh queue cycle.
        const toRun = [...ctx.effectQueue] as unknown as EffectRecord[]
        ctx.effectQueue.clear()

        for (let i = 0; i < toRun.length; i++) {
            const effect = toRun[i]
            effect.flags = clearScheduled(effect.flags)
            if (isActive(effect.flags) && !isPaused(effect.flags)) {
                // V2 effects are EffectRecord. V1 effects are ReactiveEffectLike (have .run()).
                // Detect via presence of run() method on the queued object.
                const maybeV1 = effect as unknown as { run?: () => void }
                if (typeof maybeV1.run === 'function') {
                    maybeV1.run()
                } else {
                    runEffectV2(effect)
                }
            }
        }
    } finally {
        isFlushing = false
    }
}

/**
 * Synchronously flush all pending V2 effects.
 * Mirrors the V1 `flush()` / `sync()` behavior.
 */
export function flushSchedulerSync(): void {
    const ctx = getRequestContext()
    ctx.isFlushPending = false
    flushScheduler()
}

/**
 * Run `fn` in a batch — all triggered effects are deferred until fn completes,
 * then flushed once.
 */
export function batchV2(fn: () => void): void {
    const ctx = getRequestContext()
    const wasBatching = ctx.isBatching
    ctx.isBatching = true
    try {
        fn()
    } finally {
        ctx.isBatching = wasBatching
        if (!wasBatching) flushSchedulerSync()
    }
}

// ---------------------------------------------------------------------------
// Public scheduler-v2 interface (for Phase 2 renderer integration)
// ---------------------------------------------------------------------------

/**
 * Public scheduler interface from Phase 2's plan:
 *   scheduleTask(fn, priority?)
 *   flushSync()
 *   detectSchedulerSupport()
 *
 * Phase 1 ships them as thin wrappers; Phase 2 may add scheduler.postTask
 * routing for browser priorities.
 */

export type TaskPriority = 'user-blocking' | 'user-visible' | 'background'

/**
 * Queue a task for microtask execution.
 *
 * Phase 1: wraps the task in an anonymous EffectRecord and routes through the
 * V2 scheduler. Priority is currently ignored — it's accepted so the Phase 2
 * renderer can plumb the value through without API churn.
 */
export function scheduleTask(fn: () => void, _priority?: TaskPriority): void {
    // Anonymous one-shot effect — not registered with any signal, never re-runs.
    // We avoid going through runEffectV2 since there's no dep-tracking benefit;
    // we want the task to run once on the microtask queue.
    if (typeof queueMicrotask === 'function') {
        queueMicrotask(fn)
    } else {
        Promise.resolve().then(fn)
    }
}

/**
 * Flush all pending scheduler work synchronously. Mirrors flushSchedulerSync()
 * under the Phase 2 plan's naming. Re-exported for renderer use.
 */
export function flushSync(): void {
    flushSchedulerSync()
}

/**
 * Feature detection for advanced scheduling APIs. Phase 1 returns 'microtask'
 * unconditionally; Phase 2 may upgrade this to 'scheduler.postTask' when
 * available.
 */
export function detectSchedulerSupport(): 'microtask' | 'scheduler.postTask' {
    if (typeof globalThis !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const g = globalThis as any
        if (g.scheduler && typeof g.scheduler.postTask === 'function') {
            return 'scheduler.postTask'
        }
    }
    return 'microtask'
}
